import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, ArrowRight, Sparkles, Zap, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { managePlan, managePlanLabel } from "../../lib/billing";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Projects", testId: "nav-projects" },
  { to: "/templates", label: "Templates", testId: "nav-templates" },
  { to: "/resources", label: "Resources", testId: "nav-resources" },
  { to: "/settings", label: "Settings", testId: "nav-settings" },
];

/* ------------------------------------------------------------------ *
 *  Notifications
 * ------------------------------------------------------------------ */

function buildNotifications(usage) {
  // Live, usage-derived notifications. Static system items (like Welcome) are
  // seeded into the `notifications` collection on register and merged in
  // separately at fetch time.
  const list = [];

  if (usage) {
    const used = usage.generations_used ?? 0;
    const total = usage.generations_limit ?? 5;
    const remaining = usage.generations_remaining ?? Math.max(0, total - used);
    const isPro = usage.plan === "pro";
    if (remaining === 0) {
      list.push({
        id: "runs-exhausted",
        title: "Monthly architecture runs reached",
        body: isPro
          ? "Existing projects remain available. Add more architecture runs when you need them."
          : "Existing projects remain available. Upgrade to Pro for more runs.",
        cta: { label: isPro ? "Buy more runs" : "Upgrade to Pro", to: "/pricing" },
        icon: Zap,
        tone: "danger",
        when: "This month",
      });
    } else if (remaining <= Math.max(1, Math.ceil(total * 0.1))) {
      list.push({
        id: "runs-near-limit",
        title: `${remaining} monthly architecture run${remaining === 1 ? "" : "s"} remaining`,
        body: "Your existing projects remain available if you reach this month's run limit.",
        cta: { label: "View plan", to: "/pricing" },
        icon: Zap,
        tone: "accent",
        when: "This month",
      });
    }
  }

  list.push({
    id: "templates-tip",
    title: "Try a starter template",
    body: "REST API, E-Commerce, and Serverless Pipeline templates load fully-wired in one click.",
    cta: { label: "Open templates", to: "/templates" },
    icon: LayoutTemplate,
    tone: "muted",
    when: "Tip",
  });

  return list;
}

const TONE_COLORS = {
  accent: "var(--color-accent)",
  danger: "var(--color-danger)",
  muted: "var(--color-text-secondary)",
  success: "var(--color-success)",
};

const KIND_ICONS = {
  system: Sparkles,
  project_shared: LayoutTemplate,
  digest: Bell,
};

function relativeTime(iso) {
  if (!iso) return "Now";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Now";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "Earlier";
  }
}

function adaptDbNotification(n) {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    cta: n.cta_url
      ? { label: n.cta_label || "Open", to: n.cta_url }
      : undefined,
    icon: KIND_ICONS[n.kind] || Sparkles,
    tone: n.tone || "accent",
    when: relativeTime(n.created_at),
    source: "db",
  };
}

function NotificationsBell({ usage, navigate }) {
  const derived = buildNotifications(usage);
  const [dbItems, setDbItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/me/notifications/dismissed"),
      api.get("/me/notifications"),
    ])
      .then(([dRes, nRes]) => {
        if (cancelled) return;
        setDismissed(new Set(dRes.data?.ids || []));
        setDbItems((nRes.data?.items || []).map(adaptDbNotification));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Sort: danger first, then by source order (db items come oldest-to-newest
  // is already sorted desc server-side; derived items follow).
  const TONE_RANK = { danger: 0, accent: 1, success: 2, muted: 3 };
  const merged = [...dbItems, ...derived]
    .filter((n) => !dismissed.has(n.id))
    .sort((a, b) => (TONE_RANK[a.tone] ?? 9) - (TONE_RANK[b.tone] ?? 9));
  const unread = merged.length;

  const dismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    api.post("/me/notifications/dismiss", { id }).catch(() => {
      // Optimistic UI; server reconciles on next reload.
    });
  };

  const onCta = (n) => {
    if (n.cta?.to) navigate(n.cta.to);
    dismiss(n.id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
          className="grid place-items-center motion-micro relative"
          style={{
            width: 36,
            height: 36,
            borderRadius: 9999,
            background: "transparent",
            border: "1px solid transparent",
            cursor: "pointer",
            color: "var(--color-text-secondary)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--color-border-primary)";
            e.currentTarget.style.color = "var(--color-text-primary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "transparent";
            e.currentTarget.style.color = "var(--color-text-secondary)";
          }}
        >
          <Bell size={18} strokeWidth={1.75} />
          {unread > 0 && (
            <span
              aria-hidden
              data-testid="notifications-unread-dot"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 8,
                height: 8,
                borderRadius: 9999,
                background: "var(--color-accent)",
                boxShadow: "0 0 0 2px var(--color-background-primary)",
              }}
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        data-testid="notifications-dropdown"
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          color: "var(--color-text-primary)",
          width: 360,
          padding: 0,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          className="flex items-center justify-between border-b"
          style={{
            padding: "14px 16px",
            borderColor: "var(--color-border-secondary)",
          }}
        >
          <span
            className="text-text-primary"
            style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.005em" }}
          >
            Notifications
          </span>
          <span
            className="font-mono text-text-tertiary"
            style={{ fontSize: 11, letterSpacing: "0.04em" }}
          >
            {unread} new
          </span>
        </div>

        {merged.length === 0 ? (
          <div
            className="text-center"
            style={{ padding: "32px 20px" }}
            data-testid="notifications-empty"
          >
            <Bell
              size={24}
              strokeWidth={1.5}
              color="var(--color-text-tertiary)"
              className="mx-auto mb-3"
            />
            <p
              className="text-text-primary"
              style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}
            >
              You&apos;re all caught up
            </p>
            <p className="text-text-tertiary" style={{ fontSize: 12 }}>
              We&apos;ll let you know when something happens.
            </p>
          </div>
        ) : (
          <ul className="max-h-[420px] overflow-y-auto" data-testid="notifications-list">
            {merged.map((n) => {
              const Icon = n.icon;
              const tone = TONE_COLORS[n.tone] || TONE_COLORS.accent;
              return (
                <li
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  className="border-b"
                  style={{
                    padding: "14px 16px",
                    borderColor: "var(--color-border-secondary)",
                  }}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="grid place-items-center shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 9999,
                        background: `color-mix(in oklab, ${tone} 14%, transparent)`,
                        border: `1px solid color-mix(in oklab, ${tone} 45%, transparent)`,
                        color: tone,
                      }}
                    >
                      <Icon size={15} strokeWidth={2} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className="text-text-primary truncate"
                          style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em" }}
                        >
                          {n.title}
                        </p>
                        <span
                          className="font-mono text-text-tertiary shrink-0"
                          style={{ fontSize: 10, letterSpacing: "0.04em" }}
                        >
                          {n.when}
                        </span>
                      </div>
                      <p
                        className="text-text-secondary mt-1"
                        style={{ fontSize: 12, lineHeight: 1.5 }}
                      >
                        {n.body}
                      </p>
                      <div className="mt-2 flex items-center gap-3">
                        {n.cta && (
                          <button
                            type="button"
                            onClick={() => onCta(n)}
                            data-testid={`notification-cta-${n.id}`}
                            className="motion-micro inline-flex items-center gap-1"
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              fontSize: 12,
                              fontWeight: 600,
                              color: tone,
                              cursor: "pointer",
                            }}
                          >
                            {n.cta.label}
                            <ArrowRight size={12} strokeWidth={2.25} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => dismiss(n.id)}
                          data-testid={`notification-dismiss-${n.id}`}
                          className="motion-micro"
                          style={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            fontSize: 12,
                            color: "var(--color-text-tertiary)",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-text-primary)")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-tertiary)")}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/usage")
      .then(({ data }) => {
        if (!cancelled) setUsage(data);
      })
      .catch(() => !cancelled && setUsage(null));
    return () => {
      cancelled = true;
    };
  }, []);

  const planLabel = (usage?.plan || "free").toUpperCase();
  const projectsUsed = usage?.projects_used ?? 0;
  const projectsLimit = usage?.projects_limit;

  const chatUsed = usage?.chat_used ?? 0;
  const chatLimit = usage?.chat_limit ?? 20;
  const chatWindow = usage?.chat_window || "day";
  const chatPct = Math.min(100, Math.max(0, (chatUsed / (chatLimit || 1)) * 100));
  const chatExhausted = chatUsed >= chatLimit;

  const initial = (user?.email || "?")[0]?.toUpperCase();

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    <header
      className="border-b sticky top-0 z-40"
      style={{
        background: "rgba(10,10,10,0.9)",
        backdropFilter: "blur(12px)",
        borderColor: "var(--color-border-primary)",
      }}
    >
      <div className="max-w-[1200px] mx-auto px-6 h-[64px] grid grid-cols-[1fr_auto_1fr] items-center">
        <div className="flex items-center">
          <Logo />
        </div>

        <nav className="flex items-center gap-1" data-testid="primary-nav" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              data-testid={item.testId}
              className="motion-micro"
              style={({ isActive }) => ({
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                height: 36,
                padding: "0 14px",
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 8,
                textDecoration: "none",
                background: isActive ? "var(--color-background-tertiary)" : "transparent",
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="plan-pill"
                className="motion-micro inline-flex items-center gap-1"
                style={{
                  height: 30,
                  padding: "0 12px",
                  borderRadius: 9999,
                  border: "1px solid var(--color-accent)",
                  background: "transparent",
                  color: "var(--color-accent)",
                  fontFamily: "JetBrains Mono, ui-monospace, monospace",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
                aria-label="Plan and projects"
              >
                {planLabel} <ChevronDown size={12} strokeWidth={2.25} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              data-testid="plan-dropdown"
              style={{
                background: "var(--color-background-secondary)",
                borderColor: "var(--color-border-primary)",
                color: "var(--color-text-primary)",
                width: 300,
                padding: 16,
                borderRadius: 12,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="font-mono"
                  style={{
                    padding: "3px 10px",
                    borderRadius: 9999,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    color: "var(--color-accent)",
                    background: "color-mix(in oklab, var(--color-accent) 14%, transparent)",
                    border: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
                  }}
                >
                  {planLabel}
                </span>
                <span
                  className="text-text-tertiary"
                  style={{ fontSize: 11, letterSpacing: "0.02em" }}
                >
                  {usage?.period || "current month"}
                </span>
              </div>

              <div className="flex items-baseline justify-between mb-2">
                <span
                  className="text-text-primary"
                  style={{ fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em" }}
                >
                  Active projects
                </span>
                <span
                  className="font-mono"
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: projectsLimit != null && projectsUsed >= projectsLimit ? "var(--color-danger)" : "var(--color-accent)",
                  }}
                  data-testid="plan-dropdown-credits"
                >
                  {`${projectsUsed} of ${projectsLimit ?? "unlimited"}`}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 9999,
                  background: "rgba(255,255,255,0.06)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${projectsLimit ? Math.min(100, (projectsUsed / projectsLimit) * 100) : 0}%`,
                    background: projectsLimit != null && projectsUsed >= projectsLimit ? "var(--color-danger)" : "var(--color-accent)",
                    borderRadius: 9999,
                    transition: "width 250ms cubic-bezier(0.16,1,0.3,1)",
                  }}
                />
              </div>

              <div className="flex items-baseline justify-between mt-4 mb-2">
                <span
                  className="text-text-primary"
                  style={{ fontSize: 13, fontWeight: 500, letterSpacing: "-0.005em" }}
                >
                  Chat messages
                </span>
                <span
                  className="font-mono"
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: chatExhausted ? "var(--color-danger)" : "var(--color-accent)",
                  }}
                  data-testid="plan-dropdown-chat"
                >
                  {`${chatUsed} of ${chatLimit} ${chatWindow === "day" ? "today" : "this month"}`}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 9999,
                  background: "rgba(255,255,255,0.06)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${chatPct}%`,
                    background: chatExhausted ? "var(--color-danger)" : "var(--color-accent)",
                    borderRadius: 9999,
                    transition: "width 250ms cubic-bezier(0.16,1,0.3,1)",
                  }}
                />
              </div>

              <p
                className="text-text-tertiary mt-3"
                style={{ fontSize: 12, lineHeight: 1.55 }}
              >
                {projectsLimit != null && projectsUsed >= projectsLimit
                  ? "Archive a project or upgrade to create another."
                  : "Monthly architecture runs are included with your plan. We'll notify you near the fair-use limit."}
              </p>

              <button
                type="button"
                data-testid="plan-dropdown-upgrade"
                onClick={() => managePlan(usage, navigate)}
                className="motion-micro inline-flex items-center justify-center gap-1.5 w-full mt-4"
                style={{
                  background: "var(--color-accent)",
                  color: "#0a0a0a",
                  border: "none",
                  height: 38,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--color-accent-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--color-accent)";
                }}
              >
                {managePlanLabel(usage)}
                <ArrowRight size={14} strokeWidth={2.25} />
              </button>
            </DropdownMenuContent>
          </DropdownMenu>

          <NotificationsBell usage={usage} navigate={navigate} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="motion-micro"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9999,
                  background: "var(--color-accent)",
                  color: "#0a0a0a",
                  fontFamily: "JetBrains Mono, ui-monospace, monospace",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
                aria-label="User menu"
              >
                {initial}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              style={{
                background: "var(--color-background-secondary)",
                borderColor: "var(--color-border-primary)",
                color: "var(--color-text-primary)",
              }}
            >
              <DropdownMenuLabel
                className="font-mono"
                style={{
                  fontSize: 11,
                  color: "var(--color-text-tertiary)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Signed in as
              </DropdownMenuLabel>
              <DropdownMenuLabel style={{ fontSize: 13, fontWeight: 500 }} data-testid="user-email-label">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator style={{ background: "var(--color-border-secondary)" }} />
              <DropdownMenuItem
                onClick={onLogout}
                data-testid="signout-btn"
                className="cursor-pointer"
                style={{ fontSize: 13 }}
              >
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
